import React, { useState } from 'react';
import {
  Tabs,
  Button,
  Space,
  Typography,
  Table,
  Modal,
  Form,
  Input,
  InputNumber,
  App,
  Popconfirm,
  Select,
  Card,
  Tag,
  Row,
  Col,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  TableOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useResponsive } from '../hooks/useResponsive';
import {
  useGetAreasQuery,
  useCreateAreaMutation,
  useUpdateAreaMutation,
  useDeleteAreaMutation,
  useGetTablesQuery,
  useCreateTableMutation,
  useUpdateTableMutation,
  useDeleteTableMutation,
} from '../services/tablesApi';
import { Area, Table as TableType, CreateTableRequest, UpdateTableRequest } from '../types/tables';

const { Title, Text } = Typography;
const { TextArea } = Input;

const AreasTablesAdmin: React.FC = () => {
  // App.useApp() y no el import estatico: bajo React 19 el estatico no dibuja
  const { message } = App.useApp();
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('areas');
  const [isAreaModalOpen, setIsAreaModalOpen] = useState(false);
  const [isTableModalOpen, setIsTableModalOpen] = useState(false);
  const [editingArea, setEditingArea] = useState<Area | null>(null);
  const [editingTable, setEditingTable] = useState<TableType | null>(null);
  const [areaForm] = Form.useForm();
  const [tableForm] = Form.useForm();

  // Queries
  const { data: areas = [], isLoading: loadingAreas } = useGetAreasQuery();
  const { data: tables = [], isLoading: loadingTables } = useGetTablesQuery();

  // Mutations
  const [createArea] = useCreateAreaMutation();
  const [updateArea] = useUpdateAreaMutation();
  const [deleteArea] = useDeleteAreaMutation();
  const [createTable] = useCreateTableMutation();
  const [updateTable] = useUpdateTableMutation();
  const [deleteTable] = useDeleteTableMutation();

  // ==================== AREA HANDLERS ====================

  const handleAddArea = () => {
    setEditingArea(null);
    areaForm.resetFields();
    setIsAreaModalOpen(true);
  };

  const handleEditArea = (area: Area) => {
    setEditingArea(area);
    areaForm.setFieldsValue(area);
    setIsAreaModalOpen(true);
  };

  const handleDeleteArea = async (areaId: string) => {
    try {
      await deleteArea(areaId).unwrap();
      message.success('Área eliminada correctamente');
    } catch (error: any) {
      console.error('Error deleting area:', error);
      const errorMsg = error?.status === 403
        ? 'No tienes permisos para eliminar áreas'
        : error?.data?.message || 'Error al eliminar el área. Puede que tenga mesas asignadas.';
      message.error(errorMsg, 5);
    }
  };

  const handleAreaModalOk = async () => {
    try {
      const values = await areaForm.validateFields();

      if (editingArea) {
        await updateArea({ id: editingArea.id, ...values }).unwrap();
        message.success('Área actualizada correctamente');
      } else {
        await createArea(values).unwrap();
        message.success('Área creada correctamente');
      }

      setIsAreaModalOpen(false);
      areaForm.resetFields();
      setEditingArea(null);
    } catch (error: any) {
      console.error('Error saving area:', error);
      const errorMsg = error?.status === 403
        ? 'No tienes permisos para gestionar áreas'
        : error?.data?.message || `Error al ${editingArea ? 'actualizar' : 'crear'} el área`;
      message.error(errorMsg, 5);
    }
  };

  // ==================== MOBILE RENDER FUNCTIONS ====================

  const renderAreaCards = () => (
    <Row gutter={[16, 16]}>
      {areas.map((area: Area) => {
        const tableCount = tables.filter(t => t.areaId === area.id).length;
        return (
          <Col xs={24} sm={12} key={area.id}>
            <Card
              size="small"
              title={area.name}
              extra={
                <Tag color="blue">
                  {tableCount} mesa{tableCount !== 1 ? 's' : ''}
                </Tag>
              }
              actions={[
                <Button
                  type="link"
                  icon={<EditOutlined />}
                  onClick={() => handleEditArea(area)}
                  size="small"
                >
                  Editar
                </Button>,
                <Popconfirm
                  title="¿Eliminar área?"
                  description="Esta acción no se puede deshacer. Asegúrate de que no tenga mesas asignadas."
                  onConfirm={() => handleDeleteArea(area.id)}
                  okText="Eliminar"
                  cancelText="Cancelar"
                  okButtonProps={{ danger: true }}
                >
                  <Button type="link" danger icon={<DeleteOutlined />} size="small">
                    Eliminar
                  </Button>
                </Popconfirm>,
              ]}
            >
              {area.description ? (
                <Typography.Text type="secondary">{area.description}</Typography.Text>
              ) : (
                <Typography.Text type="secondary" italic>Sin descripción</Typography.Text>
              )}
            </Card>
          </Col>
        );
      })}
    </Row>
  );

  const renderTableCards = () => (
    <Row gutter={[16, 16]}>
      {tables.map((table: TableType) => {
        const statusConfig: Record<string, { color: string; text: string }> = {
          Available: { color: 'success', text: 'Disponible' },
          Occupied: { color: 'error', text: 'Ocupada' },
          Reserved: { color: 'warning', text: 'Reservada' },
          Dirty: { color: 'default', text: 'Sucia' },
          PreCheck: { color: 'processing', text: 'Pre-cuenta' },
        };
        const config = statusConfig[table.status] || { color: 'default', text: table.status };

        return (
          <Col xs={24} sm={12} key={table.id}>
            <Card
              size="small"
              title={`Mesa ${table.number}`}
              extra={<Tag color={config.color}>{config.text}</Tag>}
              actions={[
                <Button
                  type="link"
                  icon={<EditOutlined />}
                  onClick={() => handleEditTable(table)}
                  size="small"
                >
                  Editar
                </Button>,
                <Popconfirm
                  title="¿Eliminar mesa?"
                  description="Esta acción no se puede deshacer. Asegúrate de que no tenga órdenes activas."
                  onConfirm={() => handleDeleteTable(table.id)}
                  okText="Eliminar"
                  cancelText="Cancelar"
                  okButtonProps={{ danger: true }}
                >
                  <Button type="link" danger icon={<DeleteOutlined />} size="small">
                    Eliminar
                  </Button>
                </Popconfirm>,
              ]}
            >
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div>
                  <Typography.Text type="secondary">Área:</Typography.Text>
                  <br />
                  <Tag color="green">{table.areaName}</Tag>
                </div>
                <div>
                  <Typography.Text type="secondary">Capacidad:</Typography.Text>
                  <br />
                  <Typography.Text strong>
                    {table.capacity} persona{table.capacity !== 1 ? 's' : ''}
                  </Typography.Text>
                </div>
              </Space>
            </Card>
          </Col>
        );
      })}
    </Row>
  );

  // ==================== TABLE HANDLERS ====================

  const handleAddTable = () => {
    setEditingTable(null);
    tableForm.resetFields();
    setIsTableModalOpen(true);
  };

  const handleEditTable = (table: TableType) => {
    setEditingTable(table);
    tableForm.setFieldsValue({
      number: table.number,
      areaId: table.areaId,
      capacity: table.capacity,
    });
    setIsTableModalOpen(true);
  };

  const handleDeleteTable = async (tableId: string) => {
    try {
      await deleteTable(tableId).unwrap();
      message.success('Mesa eliminada correctamente');
    } catch (error: any) {
      console.error('Error deleting table:', error);
      const errorMsg = error?.status === 403
        ? 'No tienes permisos para eliminar mesas'
        : error?.data?.message || 'Error al eliminar la mesa. Puede que tenga órdenes activas.';
      message.error(errorMsg, 5);
    }
  };

  const handleTableModalOk = async () => {
    try {
      const values = await tableForm.validateFields();

      if (editingTable) {
        await updateTable({ id: editingTable.id, ...values } as { id: string } & UpdateTableRequest).unwrap();
        message.success('Mesa actualizada correctamente');
      } else {
        await createTable(values as CreateTableRequest).unwrap();
        message.success('Mesa creada correctamente');
      }

      setIsTableModalOpen(false);
      tableForm.resetFields();
      setEditingTable(null);
    } catch (error: any) {
      console.error('Error saving table:', error);
      const errorMsg = error?.status === 403
        ? 'No tienes permisos para gestionar mesas'
        : error?.data?.message || `Error al ${editingTable ? 'actualizar' : 'crear'} la mesa`;
      message.error(errorMsg, 5);
    }
  };

  // ==================== TABLE COLUMNS ====================

  const areaColumns = [
    {
      title: 'Nombre',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      title: 'Descripción',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => text || <Text type="secondary">Sin descripción</Text>,
    },
    {
      title: 'Mesas',
      key: 'tableCount',
      render: (_: any, record: Area) => {
        const tableCount = tables.filter(t => t.areaId === record.id).length;
        return <Tag color="blue">{tableCount} mesa{tableCount !== 1 ? 's' : ''}</Tag>;
      },
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: isMobile ? 100 : 150,
      render: (_: any, record: Area) => (
        <Space size={isMobile ? 'small' : 'middle'}>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditArea(record)}
            size={isMobile ? 'small' : 'middle'}
          >
            {!isMobile && 'Editar'}
          </Button>
          <Popconfirm
            title="¿Eliminar área?"
            description="Esta acción no se puede deshacer. Asegúrate de que no tenga mesas asignadas."
            onConfirm={() => handleDeleteArea(record.id)}
            okText="Eliminar"
            cancelText="Cancelar"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size={isMobile ? 'small' : 'middle'}
            >
              {!isMobile && 'Eliminar'}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const tableColumns = [
    {
      title: 'Número',
      dataIndex: 'number',
      key: 'number',
      render: (text: string) => <Text strong>Mesa {text}</Text>,
    },
    {
      title: 'Área',
      dataIndex: 'areaName',
      key: 'areaName',
      render: (text: string) => <Tag color="green">{text}</Tag>,
    },
    {
      title: 'Capacidad',
      dataIndex: 'capacity',
      key: 'capacity',
      render: (capacity: number) => `${capacity} persona${capacity !== 1 ? 's' : ''}`,
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusConfig: Record<string, { color: string; text: string }> = {
          Available: { color: 'success', text: 'Disponible' },
          Occupied: { color: 'error', text: 'Ocupada' },
          Reserved: { color: 'warning', text: 'Reservada' },
          Dirty: { color: 'default', text: 'Sucia' },
          PreCheck: { color: 'processing', text: 'Pre-cuenta' },
        };
        const config = statusConfig[status] || { color: 'default', text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: isMobile ? 100 : 150,
      render: (_: any, record: TableType) => (
        <Space size={isMobile ? 'small' : 'middle'}>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditTable(record)}
            size={isMobile ? 'small' : 'middle'}
          >
            {!isMobile && 'Editar'}
          </Button>
          <Popconfirm
            title="¿Eliminar mesa?"
            description="Esta acción no se puede deshacer. Asegúrate de que no tenga órdenes activas."
            onConfirm={() => handleDeleteTable(record.id)}
            okText="Eliminar"
            cancelText="Cancelar"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size={isMobile ? 'small' : 'middle'}
            >
              {!isMobile && 'Eliminar'}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ==================== RENDER ====================

  return (
    <div>
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/configuracion')}
        style={{ padding: 0, marginBottom: 16 }}
      >
        Volver a Configuración
      </Button>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>Gestión de Áreas y Mesas</Title>
        <Text type="secondary">
          Administra las áreas del restaurante y configura las mesas de cada zona
        </Text>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'areas',
            label: (
              <span>
                <EnvironmentOutlined />
                {!isMobile && ' Áreas'}
              </span>
            ),
            children: (
              <Card>
                <div style={{ marginBottom: 16 }}>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAddArea}
                    size={isMobile ? 'middle' : 'large'}
                    block={isMobile}
                  >
                    {isMobile ? 'Nueva Área' : 'Nueva Área'}
                  </Button>
                </div>

                {isMobile ? (
                  loadingAreas ? (
                    <Card loading={true} />
                  ) : (
                    renderAreaCards()
                  )
                ) : (
                  <Table
                    columns={areaColumns}
                    dataSource={areas}
                    rowKey="id"
                    loading={loadingAreas}
                    pagination={{
                      pageSize: 10,
                      showSizeChanger: true,
                      showTotal: (total) => `Total: ${total} área${total !== 1 ? 's' : ''}`,
                    }}
                    scroll={{ x: 800 }}
                  />
                )}
              </Card>
            ),
          },
          {
            key: 'tables',
            label: (
              <span>
                <TableOutlined />
                {!isMobile && ' Mesas'}
              </span>
            ),
            children: (
              <Card>
                <div style={{ marginBottom: 16 }}>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAddTable}
                    size={isMobile ? 'middle' : 'large'}
                    disabled={areas.length === 0}
                    block={isMobile}
                  >
                    {isMobile ? 'Nueva Mesa' : 'Nueva Mesa'}
                  </Button>
                </div>

                {areas.length === 0 && (
                  <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                    Primero debes crear al menos un área antes de agregar mesas.
                  </Text>
                )}

                {isMobile ? (
                  loadingTables ? (
                    <Card loading={true} />
                  ) : (
                    renderTableCards()
                  )
                ) : (
                  <Table
                    columns={tableColumns}
                    dataSource={tables}
                    rowKey="id"
                    loading={loadingTables}
                    pagination={{
                      pageSize: 10,
                      showSizeChanger: true,
                      showTotal: (total) => `Total: ${total} mesa${total !== 1 ? 's' : ''}`,
                    }}
                    scroll={{ x: 900 }}
                  />
                )}
              </Card>
            ),
          },
        ]}
      />

      {/* Area Modal */}
      <Modal
        title={editingArea ? 'Editar Área' : 'Nueva Área'}
        open={isAreaModalOpen}
        onOk={handleAreaModalOk}
        onCancel={() => {
          setIsAreaModalOpen(false);
          areaForm.resetFields();
          setEditingArea(null);
        }}
        okText={editingArea ? 'Actualizar' : 'Crear'}
        cancelText="Cancelar"
        width={isMobile ? '95%' : 600}
      >
        <Form
          form={areaForm}
          layout="vertical"
          style={{ marginTop: 24 }}
        >
          <Form.Item
            label="Nombre del Área"
            name="name"
            rules={[
              { required: true, message: 'Por favor ingresa el nombre del área' },
              { min: 2, message: 'El nombre debe tener al menos 2 caracteres' },
              { max: 50, message: 'El nombre no puede exceder 50 caracteres' },
            ]}
          >
            <Input placeholder="ej. Terraza, Salón Principal, Bar" size="large" />
          </Form.Item>

          <Form.Item
            label="Descripción (opcional)"
            name="description"
            rules={[
              { max: 200, message: 'La descripción no puede exceder 200 caracteres' },
            ]}
          >
            <TextArea
              placeholder="Descripción del área..."
              rows={3}
              maxLength={200}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Table Modal */}
      <Modal
        title={editingTable ? 'Editar Mesa' : 'Nueva Mesa'}
        open={isTableModalOpen}
        onOk={handleTableModalOk}
        onCancel={() => {
          setIsTableModalOpen(false);
          tableForm.resetFields();
          setEditingTable(null);
        }}
        okText={editingTable ? 'Actualizar' : 'Crear'}
        cancelText="Cancelar"
        width={isMobile ? '95%' : 600}
      >
        <Form
          form={tableForm}
          layout="vertical"
          style={{ marginTop: 24 }}
        >
          <Form.Item
            label="Número de Mesa"
            name="number"
            rules={[
              { required: true, message: 'Por favor ingresa el número de mesa' },
              { max: 10, message: 'El número no puede exceder 10 caracteres' },
            ]}
            tooltip="Puede ser un número o código (ej. 1, T1, VIP-1)"
          >
            <Input placeholder="ej. 1, 2, 3..." size="large" />
          </Form.Item>

          <Form.Item
            label="Área"
            name="areaId"
            rules={[{ required: true, message: 'Por favor selecciona el área' }]}
          >
            <Select
              placeholder="Selecciona un área"
              size="large"
              options={areas.map(area => ({
                label: area.name,
                value: area.id,
              }))}
            />
          </Form.Item>

          <Form.Item
            label="Capacidad"
            name="capacity"
            rules={[
              { required: true, message: 'Por favor ingresa la capacidad' },
              { type: 'number', min: 1, max: 50, message: 'La capacidad debe estar entre 1 y 50' },
            ]}
            tooltip="Número máximo de personas que pueden sentarse"
          >
            <InputNumber
              placeholder="ej. 4"
              min={1}
              max={50}
              style={{ width: '100%' }}
              size="large"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AreasTablesAdmin;
